"use client";

import { ColumnDef, Row } from "@tanstack/react-table";
import { TestTube } from "lucide-react";

import {
  useLabResultsColumnFilters,
  type LabResultsWithClassification,
} from "@/src/components/cards/lab-results-card/lab-results-columns";
import { LabResultsToolbar } from "@/src/components/cards/lab-results-card/lab-results-toolbar";
import { CopyableContent } from "@/src/components/copyable-content";
import { DataTable } from "@/src/components/tables/data-table";
import { DataTableColumnHeader } from "@/src/components/tables/data-table-column-header";
import { ValerieCard } from "@/src/components/valerie-card";
import { EnabledFields } from "@/src/lib/schema-types";
import { getClassificationDisplay } from "@/src/lib/utils/classification";
import { formatDate } from "@/src/lib/utils/date-and-phone-utils";
import { isFieldEnabled, hasValue } from "@/src/lib/utils/object-utils";
import { Classification } from "@/src/models/constants/classifications";

interface LabResultsTableProps {
  labResults: LabResultsWithClassification[];
  enabledFields?: EnabledFields;
}

export function LabResultsTable({ labResults, enabledFields }: LabResultsTableProps) {
  // Get column filters and map the namespaced parameter to the column name
  const [columnFilters] = useLabResultsColumnFilters();
  const mappedColumnFilters = {
    typeOfLab: columnFilters.typeOfLab,
    needsSignature: columnFilters.needsSignature,
    classification: columnFilters.labResultsClassification,
  };

  // Data is already in the correct format
  const dataWithClassification: LabResultsWithClassification[] = labResults;

  const CellWrapper = ({ children }: { children: React.ReactNode }) => {
    return <div className="w-full p-2">{children}</div>;
  };

  const allColumns: ColumnDef<LabResultsWithClassification>[] = [
    {
      accessorKey: "classification",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Classification" />,
      cell: ({ row }: { row: Row<LabResultsWithClassification> }) => {
        const value = row.getValue("classification") as Classification;
        const displayValue = getClassificationDisplay(value);

        return (
          <CellWrapper>
            <span className="no-select">
              <CopyableContent text={displayValue} />
            </span>
          </CellWrapper>
        );
      },
      filterFn: "arrIncludesSome",
    },
    {
      accessorKey: "nameOfLab",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Lab Name" />,
      cell: ({ row }: { row: Row<LabResultsWithClassification> }) => {
        const value = row.getValue("nameOfLab") as string;

        return (
          <CellWrapper>
            <span className="max-w-[200px] break-words font-medium no-select">
              <CopyableContent text={value || "—"} />
            </span>
          </CellWrapper>
        );
      },
    },
    {
      accessorKey: "labTypesAndResults",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Type & Result" />,
      cell: ({ row }: { row: Row<LabResultsWithClassification> }) => {
        const value = row.getValue("labTypesAndResults") as Array<{ typeOfLab: string; labResult: string }> | null;

        if (!value || value.length === 0) {
          return (
            <CellWrapper>
              <span className="no-select">—</span>
            </CellWrapper>
          );
        }

        return (
          <CellWrapper>
            <div className="max-w-[500px] no-select">
              <ul className="list-disc list-inside space-y-1">
                {value.map((item, index) => (
                  <li key={index} className="text-sm">
                    <CopyableContent
                      text={`${item.typeOfLab || "N/A"}${item.labResult ? ` - ${item.labResult}` : ""}`}
                    />
                  </li>
                ))}
              </ul>
            </div>
          </CellWrapper>
        );
      },
    },
    {
      accessorKey: "collectionDate",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Collection Date" />,
      cell: ({ row }: { row: Row<LabResultsWithClassification> }) => {
        const value = row.getValue("collectionDate") as string;
        const displayValue = value ? formatDate(value) || "—" : "—";

        return (
          <CellWrapper>
            <span className="no-select">
              <CopyableContent text={displayValue} />
            </span>
          </CellWrapper>
        );
      },
    },
    {
      accessorKey: "dateOfLabResults",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Lab Date" />,
      cell: ({ row }: { row: Row<LabResultsWithClassification> }) => {
        const value = row.getValue("dateOfLabResults") as string;
        const displayValue = value ? formatDate(value) || "—" : "—";

        return (
          <CellWrapper>
            <span className="no-select">
              <CopyableContent text={displayValue} />
            </span>
          </CellWrapper>
        );
      },
    },
    {
      accessorKey: "orderingProvider",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Provider" />,
      cell: ({ row }: { row: Row<LabResultsWithClassification> }) => {
        const provider = row.getValue("orderingProvider") as any;
        const displayValue =
          provider && provider.givenName && provider.familyName ? `${provider.givenName} ${provider.familyName}` : "—";
        const npiValue = provider?.npiNumber || null;

        return (
          <CellWrapper>
            <div className="max-w-[500px] truncate font-medium no-select">
              <div>
                <CopyableContent text={displayValue} />
              </div>
              {npiValue && (
                <div className="text-sm text-muted-foreground mt-1">
                  NPI: <CopyableContent text={npiValue} />
                </div>
              )}
            </div>
          </CellWrapper>
        );
      },
      filterFn: "arrIncludesSome",
      accessorFn: (row) => {
        const provider = row.orderingProvider;

        return provider;
      },
    },
    {
      accessorKey: "listOfNameOfLabs",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Lab List" />,
      cell: ({ row }: { row: Row<LabResultsWithClassification> }) => {
        const value = row.getValue("listOfNameOfLabs") as string[];
        const displayValue = value && value.length > 0 ? value.join(", ") : "—";

        return (
          <CellWrapper>
            <span className="max-w-[200px] break-words font-medium no-select">
              <CopyableContent text={displayValue} />
            </span>
          </CellWrapper>
        );
      },
    },
    {
      accessorKey: "bodyPart",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Body Part" />,
      cell: ({ row }: { row: Row<LabResultsWithClassification> }) => {
        const value = row.getValue("bodyPart") as string;

        return (
          <CellWrapper>
            <span className="no-select">
              <CopyableContent text={value || "—"} />
            </span>
          </CellWrapper>
        );
      },
    },
    {
      accessorKey: "needsSignature",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Signature" />,
      cell: ({ row }: { row: Row<LabResultsWithClassification> }) => {
        const value = row.getValue("needsSignature") as boolean;
        const displayValue = value !== null && value !== undefined ? (value ? "Yes" : "No") : "—";

        return (
          <CellWrapper>
            <span className="no-select">
              <CopyableContent text={displayValue} />
            </span>
          </CellWrapper>
        );
      },
      filterFn: (row, id, value) => {
        const rowValue = row.getValue(id) as boolean;

        return value.includes(rowValue ? "true" : "false");
      },
    },
    {
      accessorKey: "dateOfEvent",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Event Date" />,
      cell: ({ row }: { row: Row<LabResultsWithClassification> }) => {
        const value = row.getValue("dateOfEvent") as string;
        const displayValue = value ? formatDate(value) || "—" : "—";

        return (
          <CellWrapper>
            <span className="no-select">
              <CopyableContent text={displayValue} />
            </span>
          </CellWrapper>
        );
      },
    },
    {
      accessorKey: "dateOfDocument",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Document Date" />,
      cell: ({ row }: { row: Row<LabResultsWithClassification> }) => {
        const value = row.getValue("dateOfDocument") as string;
        const displayValue = value ? formatDate(value) || "—" : "—";

        return (
          <CellWrapper>
            <span className="no-select">
              <CopyableContent text={displayValue} />
            </span>
          </CellWrapper>
        );
      },
    },
    {
      accessorKey: "dateReceived",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Received Date" />,
      cell: ({ row }: { row: Row<LabResultsWithClassification> }) => {
        const value = row.getValue("dateReceived") as string;
        const displayValue = value ? formatDate(value) || "—" : "—";

        return (
          <CellWrapper>
            <span className="no-select">
              <CopyableContent text={displayValue} />
            </span>
          </CellWrapper>
        );
      },
    },
    {
      accessorKey: "dateSigned",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Signed Date" />,
      cell: ({ row }: { row: Row<LabResultsWithClassification> }) => {
        const value = row.getValue("dateSigned") as string;
        const displayValue = value ? formatDate(value) || "—" : "—";

        return (
          <CellWrapper>
            <span className="no-select">
              <CopyableContent text={displayValue} />
            </span>
          </CellWrapper>
        );
      },
    },
  ];

  // Filter columns to only show enabled fields that have data
  const columns = allColumns.filter((column) => {
    const fieldName = "accessorKey" in column && typeof column.accessorKey === "string" ? column.accessorKey : "";

    // Always show classification column, check others against enabled fields
    if (fieldName === "classification") return true;

    return (
      isFieldEnabled(enabledFields, fieldName) &&
      dataWithClassification.some((item) => hasValue(item[fieldName as keyof LabResultsWithClassification]))
    );
  });

  return (
    <ValerieCard className="w-full" icon={<TestTube />} title="Lab Results">
      <div className="rounded-lg overflow-hidden border">
        <DataTable
          columns={columns}
          data={dataWithClassification}
          columnFilters={mappedColumnFilters}
          ToolbarComponent={LabResultsToolbar}
          getRowUrl={() => ""}
        />
      </div>
    </ValerieCard>
  );
}
